'use client';

import PlacesAutocomplete from '@c/forms/controls/PlacesAutocomplete';
import BaseModal from '@c/modals/BaseModal';
import {
  CircleF,
  GoogleMap,
  MarkerF,
  useLoadScript,
} from '@react-google-maps/api';
import Select from '@ui/Select';
import { LocateFixed } from 'lucide-react';
import { useEffect, useState } from 'react';

type LatLng = { lat: number; lng: number };
export type VehicleLocation = { lat: number; lng: number; address: string };

const MAPS_LIBRARIES = ['places'] as 'places'[];
// Radius options (miles) offered in the dropdown.
const RADIUS_OPTIONS = [10, 25, 50, 100, 250, 500];
const MILES_TO_METERS = 1609.34;
// Geographic center of the contiguous US — fallback when no location is set.
const DEFAULT_CENTER: LatLng = { lat: 39.8283, lng: -98.5795 };
const DEFAULT_RADIUS = 50;

// Bounding box of the radius circle, so the map can frame it on change.
function getRadiusBounds(center: LatLng, radiusMeters: number) {
  const latDelta = radiusMeters / 111_320;
  const lngDelta =
    radiusMeters / (111_320 * Math.cos((center.lat * Math.PI) / 180));
  return {
    north: center.lat + latDelta,
    south: center.lat - latDelta,
    east: center.lng + lngDelta,
    west: center.lng - lngDelta,
  };
}

interface ModalContentProps {
  isLoaded: boolean;
  location: VehicleLocation | null;
  radius: number | null;
  onApply: (location: VehicleLocation, radius: number) => void;
  onClose: () => void;
}

// The modal body. Mounted fresh every time the modal opens (BaseModal
// unmounts its children when closed), so state initialises from the
// current filter values without a sync effect.
const ModalContent = ({
  isLoaded,
  location,
  radius,
  onApply,
  onClose,
}: ModalContentProps) => {
  const [address, setAddress] = useState(location?.address ?? '');
  // The map always has a center (defaults to the US) so the radius circle
  // is visible immediately — picking a place or "locate me" just moves it.
  const [center, setCenter] = useState<LatLng>(
    location ? { lat: location.lat, lng: location.lng } : DEFAULT_CENTER
  );
  const [selectedRadius, setSelectedRadius] = useState(
    radius && RADIUS_OPTIONS.includes(radius) ? radius : DEFAULT_RADIUS
  );
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [locating, setLocating] = useState(false);
  // Initial camera, frozen at mount — passing live center/zoom props to
  // <GoogleMap> would override the imperative fitBounds() calls below.
  const [initialCenter] = useState<LatLng>(center);

  const radiusMeters = selectedRadius * MILES_TO_METERS;

  // Frame the map around the radius circle whenever the center or radius
  // changes, so the whole circle stays visible.
  useEffect(() => {
    if (!map) return;
    map.fitBounds(getRadiusBounds(center, radiusMeters), 32);
  }, [map, center, radiusMeters]);

  const handleLocateMe = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const coords = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        };
        setCenter(coords);
        try {
          const res = await fetch(
            `/api/geocode?lat=${coords.lat}&lng=${coords.lng}`
          );
          const data = await res.json();
          if (data?.address) setAddress(data.address);
        } catch {
          // reverse geocode is best-effort — keep the map centered regardless
        }
        setLocating(false);
      },
      () => setLocating(false)
    );
  };

  const handleApply = () => {
    if (!address) return;
    onApply({ lat: center.lat, lng: center.lng, address }, selectedRadius);
    onClose();
  };

  return (
    <>
      <div className="flex w-[34rem] flex-col gap-[2.4rem] p-[2.4rem] sm:w-[55rem]">
        <p className="text-[1.6rem] font-medium text-[#444444]">
          Search by city, neighborhood or ZIP Code.
        </p>

        <div className="flex flex-col gap-[0.4rem]">
          <span className="text-[1.6rem] font-semibold text-black">
            Location <span className="text-brand-secondary">*</span>
          </span>
          {isLoaded ? (
            <PlacesAutocomplete
              requireLatLng
              MXAuto={false}
              setSelected={({ lat, lng, address: addr }) => {
                if (lat != null && lng != null) setCenter({ lat, lng });
                setAddress(addr);
              }}
              className="!h-[5.5rem] !rounded-[2rem]"
            />
          ) : (
            <div className="h-[4.4rem] w-full animate-pulse rounded-[1.2rem] bg-[#F1F1F1]" />
          )}
        </div>

        <div className="flex flex-col gap-[0.4rem]">
          <span className="text-[1.6rem] font-semibold text-black">
            Radius <span className="text-brand-secondary">*</span>
          </span>
          <Select
            allowEmpty={false}
            value={String(selectedRadius)}
            className="!h-[5.5rem] !rounded-[2rem]"
            options={RADIUS_OPTIONS.map((mi) => ({
              id: String(mi),
              label: `${mi} miles`,
              value: String(mi),
            }))}
            onChange={(option) => setSelectedRadius(Number(option.id))}
          />
        </div>

        <div className="relative h-[32rem] w-full overflow-hidden rounded-[0.8rem] bg-[#F1F1F1]">
          {isLoaded && (
            <GoogleMap
              mapContainerClassName="h-full w-full"
              center={initialCenter}
              zoom={9}
              onLoad={setMap}
              options={{
                disableDefaultUI: true,
                clickableIcons: false,
                gestureHandling: 'greedy',
              }}
            >
              <CircleF
                center={center}
                radius={radiusMeters}
                options={{
                  fillColor: '#000000',
                  fillOpacity: 0.15,
                  strokeColor: '#000000',
                  strokeOpacity: 0.25,
                  strokeWeight: 1,
                  clickable: false,
                }}
              />
              <MarkerF position={center} />
            </GoogleMap>
          )}
          <button
            type="button"
            onClick={handleLocateMe}
            disabled={locating}
            aria-label="Use my current location"
            className="absolute right-[1.6rem] top-[1.6rem] flex h-[4.4rem] w-[4.4rem] items-center justify-center rounded-full bg-white shadow-md transition-colors hover:bg-zinc-50 disabled:opacity-60"
          >
            <LocateFixed className="h-[2rem] w-[2rem] text-black" />
          </button>
        </div>
      </div>

      <div className="flex w-full items-center justify-end border-t border-[#D9D9D9] p-[2rem]">
        <button
          type="button"
          onClick={handleApply}
          disabled={!address}
          className="flex items-center justify-center rounded-full bg-brand-secondary px-12 py-4 text-[1.8rem] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          Apply
        </button>
      </div>
    </>
  );
};

interface VehicleLocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  location: VehicleLocation | null;
  radius: number | null;
  onApply: (location: VehicleLocation, radius: number) => void;
}

// "Change location" modal — pick a city and a search radius. The map shows a
// red center pin with a circle that resizes with the selected radius.
const VehicleLocationModal = ({
  isOpen,
  onClose,
  location,
  radius,
  onApply,
}: VehicleLocationModalProps) => {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || '',
    libraries: MAPS_LIBRARIES,
  });

  return (
    <BaseModal isOpen={isOpen} dismiss={onClose} title="Change location">
      <ModalContent
        isLoaded={isLoaded}
        location={location}
        radius={radius}
        onApply={onApply}
        onClose={onClose}
      />
    </BaseModal>
  );
};

export default VehicleLocationModal;
